import React, { useCallback, useEffect, useState } from "react";
import { CreditUpdated, SearchIcon } from "../../assets/svg";
import { useDispatch, useSelector } from "react-redux";
import { clearCredentials, setCredentials } from "../../authSlice";
import toast from "react-hot-toast";
import { format } from "date-fns";

function UpdateBalanceTable() {
  const { token } = useSelector((state) => state.auth);
  const [tableEntriesCount, setTableEntriesCount] = useState(10);
  const [allAreas, setAllAreas] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [tableFilterArea, setTableFilterArea] = useState("");
  const [ledgers, setLedgers] = useState([]);
  const [filteredLedgers, setFilteredLedgers] = useState(ledgers);
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState(null);
  const [error, setError] = useState(null);
  const [apiMsg, setApiMsg] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPage, setTotalPage] = useState(1);
  const [sortField, setSortField] = useState("customerId.name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [changedEntries, setChangedEntries] = useState([]);
  const dispatch = useDispatch();
  const [tokenExpired, setTokenExpired] = useState(false);

  const [currentSearchPage, setCurrentSearchPage] = useState(1);
  const [totalSearchPage, setTotalSearchPage] = useState(1);
  const [showSearchPagination, setShowSearchPagination] = useState(false);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const fetchLedgers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const today = new Date();

      // create a new Date object for tomorrow
      const tomorrow = new Date(today);
      tomorrow.setDate(today.getDate() + 1);

      const formattedDate = `${String(tomorrow.getMonth() + 1).padStart(
        2,
        "0"
      )}-${String(tomorrow.getDate()).padStart(2, "0")}-${tomorrow.getFullYear()}`;

      // const formattedDate = `${String(today.getMonth() + 1).padStart(
      //   2,
      //   "0"
      // )}-${String(today.getDate()).padStart(2, "0")}-${today.getFullYear()}`;
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/ledger/getLedgers?limit=${tableEntriesCount}&page=${currentPage}&date=${formattedDate}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 401) {
        setTokenExpired(true);
        return;
      }

      if (response.ok) {
        const newAccessToken = response.headers.get("Authorization");
        if (newAccessToken) {
          dispatch(setCredentials({ token: newAccessToken.split(" ")[1] }));
        }
        const ledgersData = await response.json();
        setCurrentPage(ledgersData.pagination.currentPage);
        setTotalPage(ledgersData.pagination.totalPages);
        setLedgers(ledgersData.ledgers);
      } else {
        setError("Failed to load payments");
      }
    } catch (err) {
      setError("Error fetching payments");
    } finally {
      setLoading(false);
    }
  }, [currentPage, dispatch, tableEntriesCount, token]);

  const fetchLedgersByArea = useCallback(async () => {
    setLoading(true);
    setError(null);

    const today = new Date();
    const formattedDate = `${String(today.getMonth() + 1).padStart(
      2,
      "0"
    )}-${String(today.getDate()).padStart(2, "0")}-${today.getFullYear()}`;

    let url = tableFilterArea
      ? `${process.env.REACT_APP_API_URL}/ledger/getLedgers?limit=${tableEntriesCount}&area=${tableFilterArea}&page=${currentPage}&date=${formattedDate}`
      : `${process.env.REACT_APP_API_URL}/ledger/getLedgers?limit=${tableEntriesCount}&page=${currentPage}&date=${formattedDate}`;
    try {
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (response.status === 401) {
        setTokenExpired(true);
        return;
      }

      if (response.ok) {
        const newAccessToken = response.headers.get("Authorization");
        if (newAccessToken) {
          dispatch(setCredentials({ token: newAccessToken.split(" ")[1] }));
        }
        if (data.ledgers && data.ledgers.length > 0) {
          setLedgers(data.ledgers);
          setCurrentPage(data.pagination.currentPage);
          setTotalPage(data.pagination.totalPages);
          setApiMsg("");
        } else {
          setApiMsg("No ledgers found for the given criteria");
          setLedgers([]);
        }
      } else {
        setApiMsg(data.message || "Failed to load ledgers");
        setError("Failed to load ledgers");
      }
    } catch (err) {
      setApiMsg("Error fetching ledgers");
      setError("Error fetching ledgers");
    } finally {
      setLoading(false);
    }
  }, [currentPage, dispatch, tableEntriesCount, tableFilterArea, token]);

  const searchLedgers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/ledger/getLedgers?limit=${tableEntriesCount}&page=${currentPage}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 401) {
        setTokenExpired(true);
        return;
      }

      if (response.ok) {
        const newAccessToken = response.headers.get("Authorization");
        if (newAccessToken) {
          dispatch(setCredentials({ token: newAccessToken.split(" ")[1] }));
        }
        const ledgersData = await response.json();
        setCurrentPage(ledgersData.pagination.currentPage);
        setTotalPage(ledgersData.pagination.totalPages);
        setLedgers(ledgersData.ledgers);
      } else {
        setError("Failed to load payments");
      }
    } catch (err) {
      setError("Error fetching payments");
    } finally {
      setLoading(false);
    }
  }, [currentPage, dispatch, tableEntriesCount, token]);

  useEffect(() => {
    if (tokenExpired) {
      dispatch(clearCredentials());
      return;
    }
    if (!token || token === "null") {
      dispatch(setCredentials({ token: null, username: null }));
      setError("No token available. Please log in.");
      setLoading(false);
    } else {
      fetchLedgers();
    }
  }, [token, fetchLedgers, dispatch, tokenExpired]);

  // const handleCreditChange = (e, ledgerId) => {
  //   const updatedValue = e.target.value;
  //   setFilteredLedgers((prevLedgers) =>
  //     prevLedgers.map((ledger) =>
  //       ledger._id === ledgerId
  //         ? { ...ledger, todayCredit: updatedValue }
  //         : ledger
  //     )
  //   );
  // };

  const handleCreditChange = (e, id) => {
    const { value } = e.target;

    setChangedEntries((prevEntries) => {
      const entryIndex = prevEntries.findIndex(
        (entry) => entry.customerId === id
      );

      if (value === "") {
        // If the input is empty, remove the entry from changedEntries
        return entryIndex > -1
          ? prevEntries.filter((entry) => entry.customerId !== id)
          : prevEntries;
      }

      if (entryIndex > -1) {
        // If entry exists, update its value
        const updatedEntries = [...prevEntries];
        updatedEntries[entryIndex].todayCredit = parseInt(value, 10);
        return updatedEntries;
      } else {
        // Add new entry if it doesn't exist
        return [
          ...prevEntries,
          { customerId: id, todayCredit: parseInt(value, 10) },
        ];
      }
    });
  };

  const updateBalance = async () => {
    if (loading) return;
    setLoading(true);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/ledger/update`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ data: changedEntries }),
        }
      );

      if (response.status === 401) {
        setTokenExpired(true);
        return;
      }
      if (response.ok) {
        const newAccessToken = response.headers.get("Authorization");
        if (newAccessToken) {
          dispatch(setCredentials({ token: newAccessToken.split(" ")[1] }));
        }
        console.log(response);
        fetchLedgers();
        toast.success("Balance updated successfully", {
          style: {
            border: "1px solid #021F7E",
            padding: "16px",
            color: "#0573E3",
          },
          iconTheme: {
            primary: "#021F7E",
            secondary: "#fff",
          },
          duration: 6000,
        });
        setChangedEntries([]);
      } else {
        toast.error("Failed to update balance.", {
          style: {
            border: "1px solid #FE0000",
            padding: "16px",
            color: "#FE0000",
          },
          iconTheme: {
            primary: "#FE0000",
            secondary: "#FE000099",
          },
          duration: 6000,
        });
      }
    } catch (error) {
      alert("Error updating balance.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchAreas = async () => {
      setLoading(true);
      setApiError(null);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/auth/getAllAreas`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.status === 401) {
          setTokenExpired(true);
          return;
        }

        if (response.ok) {
          const newAccessToken = response.headers.get("Authorization");
          if (newAccessToken) {
            // Update the local storage with the new access token
            localStorage.setItem("access_token", newAccessToken.split(" ")[1]);
          }
          const data = await response.json();
          setAllAreas(data || []);
        } else {
          throw new Error("Failed to fetch zimidars.");
        }
      } catch (err) {
        setApiError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (token) fetchAreas();
  }, [token, dispatch]);

  // const sortLedgers = (field) => {
  //   const order = field === sortField && sortOrder === "asc" ? "desc" : "asc";
  //   setSortField(field);
  //   setSortOrder(order);

  //   const sortedLedgers = [...filteredLedgers].sort((a, b) => {
  //     const valueA = field.includes(".")
  //       ? field.split(".").reduce((o, i) => o[i], a)
  //       : a[field];
  //     const valueB = field.includes(".")
  //       ? field.split(".").reduce((o, i) => o[i], b)
  //       : b[field];

  //     if (valueA < valueB) return order === "asc" ? -1 : 1;
  //     if (valueA > valueB) return order === "asc" ? 1 : -1;
  //     return 0;
  //   });

  //   setFilteredLedgers(sortedLedgers);
  // };

  const isEntryChanged = (customerId) =>
    changedEntries.some((entry) => entry.customerId === customerId);

  if (apiError) return <div>Error: {apiError}</div>;

  return (
    <>
      <div
        className="whiteBgDiv UserManagementPadding"
        style={{ marginTop: "25px" }}
      >
        <div className="d-flex justify-content-between align-items-center gap-2 flex-wrap">
          <div className="mainHeading">Customer List</div>
          <div className="d-flex align-items-center" style={{ gap: "9px" }}>
            <div className="formInputLabelBox">
              <select
                name="tableFilterArea"
                className="form-select"
                style={{
                  padding: "10.5px 45px 10.5px 21px",
                  minWidth: "250px",
                }}
                value={tableFilterArea}
                onChange={(e) => setTableFilterArea(e.target.value)}
              >
                <option value="">All Areas</option>
                {allAreas &&
                  allAreas.map((area) => (
                    <option key={area._id} value={area.name}>
                      {area.name}
                    </option>
                  ))}
              </select>
            </div>
            {!loading ? (
              <button className="colorBtn" onClick={fetchLedgersByArea}>
                Submit
              </button>
            ) : (
              <button className="colorBtn">Submitting ...</button>
            )}
          </div>
        </div>
        <div
          style={{ marginTop: "31px" }}
          className="d-flex justify-content-between align-items-center flex-wrap gap-2"
        >
          <div
            className="align-items-center justify-content-start mt-0 tableEntriesCount"
            style={{ gap: "3px" }}
          >
            <div className="smallHeading tableEntriesCount">Show Entries</div>
            <select
              className="form-select"
              value={tableEntriesCount}
              onChange={(e) => setTableEntriesCount(e.target.value)}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
          <div className="searchBox">
            <span
              onClick={() => {
                if (searchQuery !== "") {
                  searchLedgers();
                } else {
                  fetchLedgers();
                }
              }}
            >
              <SearchIcon />
            </span>
            <input
              type="text"
              placeholder="Search"
              value={searchQuery}
              onChange={handleSearchChange}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  searchLedgers();
                }
              }}
            />
          </div>
        </div>

        <div className="tableDiv">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">
                  <div className="sortHeading">Name</div>
                </th>
                <th scope="col">
                  <div className="sortHeading">Opening Balance</div>
                </th>
                <th scope="col">
                  <div className="sortHeading">Today’s Debit</div>
                </th>
                <th scope="col">
                  <div className="d-flex justify-content-between align-items-end gap-5">
                    <>Today’s Credit</>
                    {changedEntries.length > 0 && (
                      <button className="colorBtn" onClick={updateBalance}>
                        {loading ? "Updating ..." : "Update"}
                      </button>
                    )}
                  </div>
                </th>
                {ledgers.length > 0 && <th scope="col">Last Updated at</th>}
              </tr>
            </thead>
            {!loading ? (
              <tbody>
                {apiMsg || error ? (
                  <tr>
                    <td colSpan="5">{apiMsg || "No ledgers available"}</td>
                  </tr>
                ) : ledgers && ledgers.length > 0 ? (
                  ledgers.map((ledger, index) => {
                    const roundUp = (value) =>
                      Number.isInteger(value) ? value : Math.ceil(value);
                    const formattedTime = ledger?.updatedAt
                      ? format(new Date(ledger?.updatedAt), "dd/MM/yyyy h:mm a")
                      : "N/A";
                    return (
                      <tr key={index}>
                        <th scope="row">{ledger.customerName}</th>
                        <td>{roundUp(ledger.opening_Balance)}</td>
                        <td>{roundUp(ledger.todayDebit)}</td>
                        <td>
                          <div className="formInputLabelBox position-relative flex-row align-items-center gap-3">
                            <input
                              type="number"
                              placeholder="Enter here"
                              style={{
                                padding: "10.5px 21px",
                                minWidth: "250px",
                              }}
                              onChange={(e) =>
                                handleCreditChange(e, ledger.customerId)
                              }
                            />
                            {isEntryChanged(ledger.customerId) && (
                              <span className="creditUpdated">
                                <CreditUpdated />
                              </span>
                            )}
                          </div>
                        </td>
                        <td style={{ textAlign: "center" }}>{formattedTime}</td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="5">{apiMsg || "No ledgers available"}</td>
                  </tr>
                )}
              </tbody>
            ) : (
              <tbody>
                <tr>
                  <td colSpan="5">Loading ...</td>
                </tr>
              </tbody>
            )}
          </table>
          {totalPage > 1 && (
            <div className="d-flex gap-1 justify-content-end">
              {!showSearchPagination ? (
                <div className="tablePaginationBtnFlex">
                  {currentPage > 1 && (
                    <button
                      className="grayBtn"
                      onClick={() => setCurrentPage(currentPage - 1)}
                    >
                      Back
                    </button>
                  )}
                  {totalPage !== currentPage && (
                    <button
                      className="colorBtn"
                      onClick={() => setCurrentPage(currentPage + 1)}
                    >
                      Next
                    </button>
                  )}
                </div>
              ) : (
                <div className="tablePaginationBtnFlex">
                  {currentSearchPage > 1 && (
                    <button
                      className="grayBtn"
                      onClick={() =>
                        setCurrentSearchPage(currentSearchPage - 1)
                      }
                    >
                      Back
                    </button>
                  )}
                  {totalSearchPage !== currentSearchPage && (
                    <button
                      className="colorBtn"
                      onClick={() =>
                        setCurrentSearchPage(currentSearchPage + 1)
                      }
                    >
                      Next
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default UpdateBalanceTable;
